import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

// Factures de commission d'apport d'affaires (plusieurs partenaires par projet)
const CommissionInvoicesCard = ({ projectId, invoices = [], canEdit, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ partner_name: '', partner_email: '', amount_ht: '', sent_at: '' });
  const [saving, setSaving] = useState(false);
  const [apporteurs, setApporteurs] = useState([]);

  useEffect(() => {
    if (!adding) return;
    axios.get(`${API}/apporteurs`).then(r => setApporteurs(r.data.items || [])).catch(() => {});
  }, [adding]);

  const addInvoice = async () => {
    if (!form.partner_name.trim()) { alert('Indiquez le nom du partenaire / intervenant.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/commission-invoices`, {
        partner_name: form.partner_name.trim(),
        partner_email: form.partner_email.trim() || null,
        amount_ht: form.amount_ht === '' ? null : parseFloat(form.amount_ht),
        sent_at: form.sent_at ? `${form.sent_at}T12:00:00Z` : null,
      });
      setForm({ partner_name: '', partner_email: '', amount_ht: '', sent_at: '' });
      setAdding(false);
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur lors de l'ajout de la facture");
    } finally { setSaving(false); }
  };

  const updateInvoice = async (invoiceId, patch) => {
    try {
      await axios.put(`${API}/projects/${projectId}/commission-invoices/${invoiceId}`, patch);
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Supprimer la facture d'apport « ${inv.partner_name} » ?`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/commission-invoices/${inv.invoice_id}`);
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-3" data-testid="commission-invoices-section">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">Factures de commission d'apport</span>
        {canEdit && (
          <button onClick={() => setAdding(a => !a)}
                  className="text-xs px-2.5 py-1 rounded-lg bg-[#13736D] hover:bg-[#0f5f5a] text-white font-semibold"
                  data-testid="add-commission-invoice-btn">
            {adding ? 'Annuler' : '+ Facture d\'apport'}
          </button>
        )}
      </div>

      {adding && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="commission-invoice-form">
          {apporteurs.length > 0 && (
            <div data-testid="apporteurs-quickpick">
              <p className="text-[10px] uppercase text-gray-400 font-semibold mb-1">Partenaires connus — 1 clic pour pré-remplir</p>
              <div className="flex flex-wrap gap-1.5">
                {apporteurs.map(a => (
                  <button key={a.apporteur_id} type="button"
                          onClick={() => setForm({ ...form, partner_name: a.name, partner_email: a.email || '' })}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${form.partner_name === a.name ? 'bg-[#13736D] text-white border-[#13736D]' : 'bg-white border-gray-300 text-gray-700 hover:border-[#13736D] hover:text-[#13736D]'}`}
                          data-testid={`apporteur-chip-${a.apporteur_id}`}>
                    {a.name}{a.email ? '' : ' (sans email)'}
                  </button>
                ))}
              </div>
            </div>
          )}
          <input type="text" placeholder="Partenaire / intervenant (ex. Cabinet Dupont) *"
                 value={form.partner_name} onChange={e => setForm({ ...form, partner_name: e.target.value })}
                 className="realease-input text-sm w-full" data-testid="commission-invoice-partner-input" />
          <input type="email" placeholder="Email du partenaire (pour les relances auto)"
                 value={form.partner_email} onChange={e => setForm({ ...form, partner_email: e.target.value })}
                 className="realease-input text-sm w-full" data-testid="commission-invoice-email-input" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Montant HT</label>
              <input type="number" placeholder="Ex. 4500" value={form.amount_ht}
                     onChange={e => setForm({ ...form, amount_ht: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="commission-invoice-amount-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Envoyée le</label>
              <input type="date" value={form.sent_at}
                     onChange={e => setForm({ ...form, sent_at: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="commission-invoice-sentdate-input" />
            </div>
          </div>
          <button onClick={addInvoice} disabled={saving}
                  className="w-full text-sm py-1.5 rounded-lg bg-[#13736D] hover:bg-[#0f5f5a] text-white font-semibold disabled:opacity-50"
                  data-testid="commission-invoice-save-btn">
            {saving ? 'Ajout…' : 'Ajouter la facture'}
          </button>
        </div>
      )}

      {invoices.length === 0 && !adding && (
        <p className="text-xs text-gray-400 italic" data-testid="commission-invoices-empty">
          Aucune facture d'apport. Ajoutez une facture par partenaire pour activer les relances automatiques.
        </p>
      )}

      {invoices.map(inv => (
        <div key={inv.invoice_id}
             className={`rounded-lg border p-3 space-y-2 ${inv.paid ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}
             data-testid={`commission-invoice-${inv.invoice_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{inv.partner_name || 'Partenaire'}</p>
              {canEdit ? (
                <input type="email" placeholder="Email partenaire (relances auto)"
                       defaultValue={inv.partner_email || ''}
                       onBlur={e => { const v = e.target.value.trim(); if (v !== (inv.partner_email || '')) updateInvoice(inv.invoice_id, { partner_email: v || null }); }}
                       className="mt-0.5 w-full text-xs border-b border-dashed border-gray-300 bg-transparent focus:outline-none focus:border-[#13736D] py-0.5"
                       data-testid={`commission-invoice-email-${inv.invoice_id}`} />
              ) : (
                <p className="text-xs text-gray-500">{inv.partner_email || 'Email non renseigné'}</p>
              )}
            </div>
            {canEdit && (
              <button onClick={() => deleteInvoice(inv)} title="Supprimer"
                      className="text-gray-300 hover:text-red-500 text-sm px-1"
                      data-testid={`commission-invoice-delete-${inv.invoice_id}`}>✕</button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Montant HT</label>
              <input type="number" disabled={!canEdit} defaultValue={inv.amount_ht ?? ''}
                     placeholder="à renseigner"
                     onBlur={e => { const v = e.target.value === '' ? null : parseFloat(e.target.value); if (v !== (inv.amount_ht ?? null) && (v === null || !isNaN(v))) updateInvoice(inv.invoice_id, { amount_ht: v }); }}
                     className="realease-input text-sm w-full" data-testid={`commission-invoice-amount-${inv.invoice_id}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Facture envoyée le</label>
              <input type="date" disabled={!canEdit}
                     value={inv.sent_at ? inv.sent_at.split('T')[0] : ''}
                     onChange={e => updateInvoice(inv.invoice_id, { sent_at: e.target.value ? `${e.target.value}T12:00:00Z` : null })}
                     className="realease-input text-sm w-full" data-testid={`commission-invoice-sentdate-${inv.invoice_id}`} />
            </div>
          </div>
          <label className={`flex items-center gap-2 cursor-pointer rounded-lg border px-2 py-1.5 ${inv.paid ? 'bg-emerald-100 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
            <input type="checkbox" checked={!!inv.paid} disabled={!canEdit}
                   onChange={e => updateInvoice(inv.invoice_id, { paid: e.target.checked })}
                   data-testid={`commission-invoice-paid-${inv.invoice_id}`} />
            <span className={`text-sm font-semibold ${inv.paid ? 'text-emerald-700' : 'text-gray-700'}`}>
              Réglée (encaissée)
              {inv.paid && inv.paid_at && (
                <span className="font-normal text-xs text-emerald-600"> — le {new Date(inv.paid_at).toLocaleDateString('fr-FR')}</span>
              )}
            </span>
          </label>
          {!inv.paid && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
               data-testid={`commission-invoice-reminders-${inv.invoice_id}`}>
              Relances auto <b>J+10</b>, <b>J+20</b>, <b>J+30</b> puis tous les 10 jours (partenaire + Amélie, + Jean dès J+30),
              à partir de la date d'envoi de la facture{!inv.sent_at && " (sinon de l'acte)"}.
              {!inv.partner_email && <span className="block mt-0.5 font-semibold">⚠ Email partenaire manquant : la relance n'ira qu'à Amélie.</span>}
              {(inv.reminders || []).filter(r => r.emailed !== false).length > 0 && (
                <span className="block mt-1">
                  Déjà envoyées : {(inv.reminders || []).filter(r => r.emailed !== false).map(r => `J+${r.offset}`).join(', ')}
                </span>
              )}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default CommissionInvoicesCard;
